import { apiFetch } from './client';

/** Ce que le serveur RECALCULE à partir des questions authorées. */
export type ResultatQuiz = {
  score: number;
  correct: number;
  total: number;
  passed: boolean | null;
};

/**
 * Le joueur a RENDU son quiz : on envoie ses réponses — CCT-427.
 *
 * ⚠️ ON ENVOIE LES RÉPONSES, PAS LE VERDICT. Le navigateur détient les bonnes réponses (il lui
 * faut bien, pour afficher les explications) : un score qu'il rapporte est un score qu'il
 * choisit. Le serveur note contre l'authoring, et range à côté ce que nous avons annoncé — pour
 * qu'un désaccord se lise au lieu de se trancher en silence.
 *
 * ⚠️ ET CE N'EST PAS UN VERROU. Avant ce mécanisme, un quiz joué ne laissait AUCUNE trace :
 * ni réponse, ni score, ni tentative. Mesuré sur un parcours complet à deux joueurs — trois
 * quiz ouverts, trois quiz marqués faits, et rien en base. Le résultat s'affiche donc sans
 * attendre cette réponse : un échec réseau coûte une ligne de journal, jamais l'écran du joueur.
 */
/** What the server recomputes from the authored chat — CCT-471. */
export type ResultatChat = {
  /** `null` when the chat holds no scorable question: no score, and nothing to compare. */
  score: number | null;
  correct: number;
  total: number;
  passed: boolean;
  threshold: number | null;
};

/**
 * The player finished a chat: we send their CHOICES and the server grades them — CCT-471.
 *
 * ⚠️ UNLIKE THE QUIZ CALL, THIS ONE IS THE GATE. A chat carrying a threshold is no longer
 * completed on opening, so this answer is what marks the element done. A network failure must
 * therefore leave it INCOMPLETE rather than be swallowed: returning null lets the screen say the
 * result could not be sent, instead of showing a conversation that looks finished and a chapter
 * that never opens.
 *
 * ⚠️ WE SEND LABELS, NOT INDEXES. The option label is what `optionResponses` is already keyed on
 * server side; an index would break the moment an author inserts a choice.
 */
export async function envoyerResultatChat(
  token: string | null,
  missionId: number,
  itemId: string,
  choices: Record<string, string>,
  score: number | null,
): Promise<ResultatChat | null> {
  try {
    const r = await apiFetch(
      `/me/stories/${missionId}/items/${encodeURIComponent(itemId)}/chat-result`,
      { method: 'POST', token, auth: true, body: { choices, score } },
    );
    return (r as ResultatChat) ?? null;
  } catch {
    return null;
  }
}

export async function envoyerReponsesQuiz(
  token: string | null,
  missionId: number,
  itemId: string,
  answers: { [questionId: number]: number[] },
  score: number,
): Promise<ResultatQuiz | null> {
  try {
    const r = await apiFetch(
      `/me/stories/${missionId}/items/${encodeURIComponent(itemId)}/quiz-answers`,
      { method: 'POST', token, auth: true, body: { answers, score } },
    );
    return (r as ResultatQuiz) ?? null;
  } catch {
    return null;
  }
}
